import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, Select, message } from 'antd';
import { LockOutlined, SafetyOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authService, CaptchaResponse } from '../services/authService';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelect from '../components/LanguageSelect';
import { authStorage } from '../utils/authStorage';
import { EDISON_LOGO_URL } from '../constants/branding';

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captcha, setCaptcha] = useState<CaptchaResponse | null>(null);

  const fetchCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const data = await authService.getCaptcha();
      setCaptcha(data);
      form.setFieldValue('captchaCode', '');
    } catch (error: any) {
      message.error(error.message || t('captchaFailed'));
    } finally {
      setCaptchaLoading(false);
    }
  };

  useEffect(() => {
    if (authStorage.isLoggedIn()) {
      navigate('/', { replace: true });
      return;
    }
    fetchCaptcha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values: any) => {
    if (!captcha?.captchaId) {
      await fetchCaptcha();
      return;
    }

    setLoading(true);
    try {
      await authService.login({
        loginName: values.loginName,
        password: values.password,
        captchaId: captcha.captchaId,
        captchaCode: values.captchaCode,
      });
      message.success(t('loginSuccess'));
      navigate('/', { replace: true });
    } catch (error: any) {
      message.error(error.message || t('loginFailed'));
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-language">
        <LanguageSelect style={{ width: 140 }} />
      </div>

      <Card className="login-card">
        <div className="login-brand" data-i18n-ignore="true">
          <img src={EDISON_LOGO_URL} alt="Edison" className="login-brand-logo" />
          <div>
            <h1>{t('appName')}</h1>
            <p className="login-brand-subtitle">{t('loginTitle')}</p>
            <p>{t('loginSubtitle')}</p>
          </div>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
          <Form.Item
            label={t('loginName')}
            name="loginName"
            rules={[{ required: true, message: t('requiredLoginName') }]}
          >
            <Input prefix={<UserOutlined />} placeholder={t('loginNamePlaceholder')} size="large" />
          </Form.Item>

          <Form.Item
            label={t('password')}
            name="password"
            rules={[{ required: true, message: t('requiredPassword') }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder={t('passwordPlaceholder')} size="large" />
          </Form.Item>

          <Form.Item
            label={t('captcha')}
            name="captchaCode"
            rules={[{ required: true, message: t('requiredCaptcha') }]}
          >
            <Input
              prefix={<SafetyOutlined />}
              placeholder={t('captchaPlaceholder')}
              size="large"
              suffix={
                <button
                  type="button"
                  className="captcha-button"
                  title={t('refreshCaptcha')}
                  onClick={fetchCaptcha}
                  disabled={captchaLoading}
                >
                  {captcha?.captchaImage ? (
                    <img src={captcha.captchaImage} alt={t('captcha')} />
                  ) : (
                    t('captcha')
                  )}
                </button>
              }
            />
          </Form.Item>

          <Button type="primary" htmlType="submit" size="large" loading={loading} block>
            {t('login')}
          </Button>
        </Form>
      </Card>
    </div>
  );
};

export default Login;
